import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import * as Sentry from "@sentry/nextjs";
import { SpanAttributes } from "@sentry/core";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function isNonEmptyArray<A>(arr: Array<A>): arr is NonEmptyArray<A> {
  return arr.length > 0;
}

export function isRecord<A>(obj: unknown): obj is Record<string, A> {
  return typeof obj === "object" && obj !== null && !Array.isArray(obj);
}

export function isNonEmptyRecord<A>(
  obj: unknown
): obj is NonEmptyRecord<string, A> {
  return (
    typeof obj === "object" &&
    obj !== null &&
    !Array.isArray(obj) &&
    Object.keys(obj).length > 0
  );
}

type SpanOptions = {
  /** Custom attributes to attach to the span */
  attributes?: SpanAttributes;
};

export enum SpanStatusCode {
  OK = 1,
  ERROR = 2,
}

export type Span = Sentry.Span;

/**
 * Creates a tracer function that wraps operations with Sentry performance monitoring
 *
 * @param operationSource - The source/category of the operation (e.g., 'db.query', 'http.request')
 * @returns A function that wraps operations with Sentry span tracking
 *
 * @example
 * const withTracing = createTracer('db.query');
 *
 * // Basic usage
 * withTracing(() => {
 *   return db.users.findMany();
 * }, 'find-users');
 *
 * // With span access
 * withTracing((span) => {
 *   span?.setAttributes({ 'db.table': 'users' });
 *   return db.users.findMany();
 * }, 'find-users');
 */
export function createTracer(operationSource: string) {
  ensureSentryInitialized();
  return function withTracing<T>(
    operation: (span: Sentry.Span) => T,
    spanName: string,
    spanOptions?: SpanOptions
  ): T {
    return Sentry.startSpan(
      {
        name: spanName,
        op: operationSource,
        attributes: spanOptions?.attributes,
      },
      (span) => {
        try {
          const result = operation(span);
          span.setStatus({ code: SpanStatusCode.OK });
          return result;
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : String(error);

          span.setStatus({
            code: SpanStatusCode.ERROR,
            message: errorMessage,
          });

          Sentry.captureException(error, {
            tags: {
              operationSource,
              spanName,
            },
          });

          throw error;
        }
      }
    );
  };
}

export function ensureSentryInitialized() {
  if (!Sentry.getClient()) {
    Sentry.init({
      dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
      tracesSampleRate: 1,
    });
    return true;
  }
  return false;
}

export function groupArrayOfObjectsByKey<T extends Record<string, unknown>>(
  objects: NonEmptyArray<T>,
  key: FieldPath<T>
): NonEmptyRecord<string, NonEmptyArray<T>> {
  const result = objects.reduce(
    (acc, object) => {
      // Use getNestedValue to safely access nested properties
      const value = getNestedValue(object, key);

      // Convert the value to string to ensure it's a valid object key
      const keyValue = String(value);

      if (!acc[keyValue]) {
        acc[keyValue] = [object];
      } else {
        acc[keyValue].push(object);
      }

      return acc;
    },
    {} as Record<string, NonEmptyArray<T>>
  );

  if (isNonEmptyRecord(result)) {
    return result;
  }

  throw new Error("Unexpected output: no objects grouped by key");
}

/**
 * Generic function to convert an array of objects into a record where
 * keys are extracted using the keyFn and values are arrays collected using valueFn
 *
 * @example
 * // From: [{type: "userId", value: "1"}, {type: "userId", value: "2"}, {type: "traits.email", value: "blah"}]
 * // To: {userId: ["1", "2"], "traits.email": ["blah"]}
 * const result = groupByKeyWithValues(
 *   items,
 *   item => item.type,
 *   item => item.value
 * );
 */
export function groupByKeyWithValues<T, K extends string | number | symbol, V>(
  items: NonEmptyArray<T>,
  keyFn: (item: T) => K,
  valueFn: (item: T) => V
): Record<K, NonEmptyArray<V>> {
  const result = {} as Record<K, NonEmptyArray<V>>;

  for (const item of items) {
    const key = keyFn(item);
    const value = valueFn(item);

    if (!result[key]) {
      result[key] = [value];
    } else {
      result[key].push(value);
    }
  }

  return result;
}

/**
 * Gets a nested value from an event object using the provided path
 * Handles errors gracefully and checks for circular references.
 * @param event - the event object
 * @param path - the path to the value in the event object. eg `context.device.id` or `traits.emails.0.value`
 * @returns the value at the path, or undefined if the path is invalid
 */

export function getNestedValue<T extends Record<string, unknown>>(
  event: T,
  path: FieldPath<T>
): unknown {
  if (!path) {
    return event;
  }

  const visited = new Set<object>();
  const parts = path.split(".");
  let current: unknown = event;

  for (const part of parts) {
    if (current == null) {
      return undefined;
    }

    if (typeof current === "object") {
      if (visited.has(current)) {
        return undefined;
      }
      visited.add(current);

      // Handle array access with numeric indices
      if (Array.isArray(current) && !isNaN(Number(part))) {
        const index = Number(part);
        current = current[index];
      } else if (isRecord(current) && part in current) {
        current = current[part];
      } else {
        return undefined;
      }
    } else {
      return undefined;
    }
  }

  return current;
}

/**
 * Checks if two objects have a matching property.
 * Comparison is done based on the provided property (or properties) values.
 */
export function objectsHaveMatchingProperty<T extends Record<string, unknown>>(
  a: T,
  b: T,
  property: keyof T | Array<keyof T>
): boolean {
  if (typeof property === "string") {
    return a[property] === b[property];
  }
  if (Array.isArray(property)) {
    return property.every((p) => a[p] === b[p]);
  }
  return false;
}

/**
 * djb2 hash → unsigned 32-bit → base36
 * Produces consistent 4–6 char strings for typical inputs.
 */
export function hashString(str: string) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    // hash * 33 + str.charCodeAt(i)
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
  }
  // >>> 0: force unsigned 32-bit, then base36 for compactness
  return (hash >>> 0).toString(36);
}
